"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { ReportDocument } from "./ReportDocument";
import { RiskChip } from "./RiskChip";
import { REPORTS, getReport } from "@/lib/reports";
import { CATEGORIES } from "@/lib/library";
import { REPORT_TRIGGERS, evaluateTriggers, controlledHazards, applyTriggerContent, NOT_SURE, type TriggerQuestion } from "@/lib/triggers";
import { riskScore } from "@/lib/templateContent";
import { buildPdfHtml } from "@/lib/pdfHtml";
import { applyEdits, editsSummary, originalText, isNonRemovable, type DocEdits } from "@/lib/edits";
import { AddressField } from "./AddressField";
import { track, googleConversion, identify, phContext } from "@/lib/analytics";
import { setBuilderState, setLastReport } from "@/lib/pageContext";
import { GOOGLE_ADS } from "@/lib/site";
import { SitePlanEditor, SitePlanPrint, findCalibration, useSitePlan, useSitePlanBackground } from "@/components/tools/SitePlanCanvas";
import { BuilderReview } from "./BuilderReview";
import { BrandingControls } from "./BrandingControls";
import { EMPTY_BRAND, sanitizeBrand, type Brand } from "@/lib/brand";

const EMAIL_KEY = "ramsdocs_email";
/** Whole working draft (same browser) so a returning user resumes where they left off. */
const DRAFT_KEY = "ramsdocs_builder_draft";
/** Document branding (logo + accent) — account-level, shared across all the user's RAMS. */
const BRAND_KEY = "ramsdocs_brand";
/** Landing default — the plumbing & heating flagship (validation-lead trade). */
const DEFAULT_REPORT = "boiler-installation";
const DATE_FIELDS = new Set(["startDate", "reviewDate"]);
const TEXTAREA_FIELDS = new Set(["accessRestrictions", "nearbyTradesOrPublic", "welfareArrangements"]);

/** Pre-submission confirmations a PC reviewer expects but the builder can't
 *  derive — the user ticks them off; they feed the readiness checklist. */
const CONFIRM_ITEMS = [
  { key: "siteRules", label: "Site rules / construction phase plan reviewed and reflected in this RAMS" },
  { key: "briefing", label: "All operatives will be briefed and sign before work starts" },
  { key: "records", label: "Training & competence records available on request" },
] as const;

const YESNO_LABEL: Record<string, string> = { yes: "Yes", no: "No", [NOT_SURE]: "Not sure" };

function readinessStatus(readiness: number): "Draft" | "Needs confirmation" | "Ready for review" {
  if (readiness >= 100) return "Ready for review";
  if (readiness >= 65) return "Needs confirmation";
  return "Draft";
}

function reviewerAction(label: string): string {
  if (label.includes("Site address")) return "Site address missing - reviewers need to know which workface this RAMS applies to.";
  if (label.includes("Client") || label.includes("principal contractor")) return "Client / principal contractor missing - name who will review or control the site.";
  if (label.includes("Start date")) return "Start date missing - add the planned work date so the RAMS is a controlled, current document.";
  if (label.includes("Prepared by")) return "Prepared-by field missing - identify who drafted or owns the RAMS.";
  if (label.includes("supervisor") || label.includes("competent person")) return "Supervisor / competent person missing - reviewers expect a named point of control.";
  if (label.includes("Review date")) return "Review date missing - add when this RAMS must be checked or revised.";
  if (label.includes("Job questions answered")) return "Job questions incomplete - answer the site and risk-trigger questions so the controls match the job.";
  if (label.includes("At least one hazard")) return "No hazards in scope - confirm the actual hazards for the task before issue.";
  if (label.includes("No high residual risks")) return "High residual risk still in scope - reduce the risk or hold the work for competent review.";
  if (label.includes("Site rules")) return "Site rules / CPP not confirmed - check the principal contractor's requirements before submission.";
  if (label.includes("operatives will be briefed")) return "Briefing not confirmed - operatives must be briefed and sign before work starts.";
  if (label.includes("Training & competence")) return "Competence records not confirmed - make sure training, cards and appointments can be evidenced.";
  if (label.includes("Site plan")) return "Site plan sketch attached.";
  return label;
}

/** Sketcher plan preset per report category — categories without a natural preset start on all layers. */
const CATEGORY_PRESET: Record<string, string> = {
  "work-at-height": "height",
  "lifting-plant": "lift",
  "hot-works": "hotworks",
  groundworks: "excavation",
  "site-general": "traffic",
};

/**
 * The report builder: pick a category then a report, fill in site details,
 * answer the job trigger questions (they set the hazard scope, permits and
 * job-specific notes), fine-tune hazards under Advanced — the document
 * re-renders live on every change. Download (print-to-PDF) is unlocked with an
 * email via the existing signup worker.
 */
export function ReportBuilder() {
  const [slug, setSlug] = useState(DEFAULT_REPORT);
  const [query, setQuery] = useState("");
  const [values, setValues] = useState<Record<string, string>>({});
  const [answers, setAnswers] = useState<Record<string, string>>({});
  /** Manual hazard overrides on top of question-derived scope (Advanced). */
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  /** In-document text edits ("" = removed) — every change is captured. */
  const [edits, setEdits] = useState<DocEdits>({});
  /** Manual pre-submission confirmations (CONFIRM_ITEMS). */
  const [confirms, setConfirms] = useState<Record<string, boolean>>({});
  /** Delivery email — required for download; the PDF is also emailed to it. */
  const [savedEmail, setSavedEmail] = useState("");
  /** Stamped client-side (today) — in state to avoid an SSR hydration mismatch. */
  const [issueDate, setIssueDate] = useState("");
  const [gateOpen, setGateOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [gateStatus, setGateStatus] = useState<"idle" | "loading" | "error">("idle");
  const [emailedTo, setEmailedTo] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestText, setRequestText] = useState("");
  const [requestEmail, setRequestEmail] = useState("");
  const [requestStatus, setRequestStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackChoice, setFeedbackChoice] = useState("");
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackStatus, setFeedbackStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  /** Gates draft persistence until any saved draft has been restored, so the
   *  initial empty state can't overwrite it on first mount. */
  const hydrated = useRef(false);
  /** True once the mount effect has applied any ?report= deep link / saved
   *  draft — used to lift the pre-paint hide set by app/builder/page.tsx. */
  const [entryResolved, setEntryResolved] = useState(false);
  /** Document branding (logo + accent colour), persisted across all the user's RAMS. */
  const [brand, setBrand] = useState<Brand>(EMPTY_BRAND);
  const brandHydrated = useRef(false);

  const entry = getReport(slug)!;
  const report = entry.data;
  const triggers = REPORT_TRIGGERS[slug] || [];

  /** Optional site plan sketch — stored per report so jobs don't bleed into each other. */
  const [sitePlan, setSitePlan] = useSitePlan(`builder-${slug}`);
  const [sketchBg, setSketchBg] = useSitePlanBackground(`builder-${slug}`);
  const [sketchOpen, setSketchOpen] = useState(false);
  const hasSketch = sitePlan.length > 0 || !!sketchBg?.dataUrl;

  // Restore a saved draft (same browser) + previously unlocked email, honour a
  // ?report= deep link, and stamp today's issue date.
  useEffect(() => {
    const wanted = new URLSearchParams(window.location.search).get("report");
    const deepLinked = !!(wanted && getReport(wanted));
    let restoredSlug = "";
    let restoredDate = "";
    try {
      const raw = window.localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        // A fresh deep link to a *different* report starts clean; otherwise the
        // saved draft wins so the user picks up exactly where they left off.
        if (d && d.slug && getReport(d.slug) && (!deepLinked || wanted === d.slug)) {
          setSlug(d.slug);
          if (d.values) setValues(d.values);
          if (d.answers) setAnswers(d.answers);
          if (d.overrides) setOverrides(d.overrides);
          if (d.edits) setEdits(d.edits);
          if (d.confirms) setConfirms(d.confirms);
          restoredSlug = d.slug;
          restoredDate = typeof d.issueDate === "string" ? d.issueDate : "";
          if (d.slug !== DEFAULT_REPORT) window.history.replaceState(null, "", `?report=${d.slug}`);
        }
      }
    } catch {
      /* private mode / corrupt draft — start fresh */
    }
    if (!restoredSlug && deepLinked && wanted !== DEFAULT_REPORT) selectReport(wanted, false);
    setIssueDate(restoredDate || new Date().toISOString().slice(0, 10));
    try {
      const stored = window.localStorage.getItem(EMAIL_KEY) || "";
      if (stored.includes("@")) {
        setSavedEmail(stored);
        setEmail(stored);
      }
    } catch {
      /* private mode — gate every download */
    }
    try {
      const rawBrand = window.localStorage.getItem(BRAND_KEY);
      if (rawBrand) setBrand(sanitizeBrand(JSON.parse(rawBrand)));
    } catch {
      /* private mode / corrupt brand — no branding */
    }
    const initialReport = restoredSlug || (deepLinked ? wanted : DEFAULT_REPORT);
    const entryKind = restoredSlug ? "resumed_draft" : deepLinked ? "template_or_deep_link" : "default";
    track("builder_viewed", { report: initialReport, entry: entryKind });
    track("builder_entry_loaded", { report: initialReport, entry: entryKind });
    setEntryResolved(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lift the pre-paint hide only after the commit that applied the deep-linked
  // report, so the default document never paints under a ?report= URL.
  useEffect(() => {
    if (entryResolved) document.documentElement.classList.remove("builder-deeplink-pending");
  }, [entryResolved]);

  // Persist the working draft on every change so a returning user resumes it.
  // The first run (initial mount) is skipped so the empty state can't clobber a
  // saved draft before the restore effect above has re-rendered with its values.
  useEffect(() => {
    if (!hydrated.current) {
      hydrated.current = true;
      return;
    }
    try {
      window.localStorage.setItem(
        DRAFT_KEY,
        JSON.stringify({ slug, values, answers, overrides, edits, confirms, issueDate }),
      );
    } catch {
      /* private mode / quota — draft just won't persist */
    }
  }, [slug, values, answers, overrides, edits, confirms, issueDate]);

  // Persist branding (logo + accent) across all the user's RAMS. First run skipped
  // so the empty default can't clobber a saved brand before it's loaded above.
  useEffect(() => {
    if (!brandHydrated.current) {
      brandHydrated.current = true;
      return;
    }
    try {
      window.localStorage.setItem(BRAND_KEY, JSON.stringify(brand));
    } catch {
      /* private mode / quota — branding just won't persist */
    }
  }, [brand]);

  function selectReport(nextSlug: string, fireEvent = true) {
    setSlug(nextSlug);
    setValues({});
    setAnswers({});
    setOverrides({});
    setEdits({});
    setConfirms({});
    setSketchOpen(false);
    window.history.replaceState(null, "", `?report=${nextSlug}`);
    if (fireEvent) {
      const next = getReport(nextSlug);
      track("builder_report_selected", { report: nextSlug });
      track("builder_task_selected", { report: nextSlug, category: next?.category || "", trade: next?.trade || "" });
    }
  }

  function setField(key: string, value: string) {
    setValues((prev) => ({ ...prev, [key]: value }));
  }

  function answerQuestion(q: TriggerQuestion, value: string) {
    setAnswers((prev) => ({ ...prev, [q.key]: value }));
    // The question wins over earlier manual tweaks to the hazards it controls.
    setOverrides((prev) => {
      const next = { ...prev };
      for (const id of controlledHazards(q)) delete next[id];
      return next;
    });
    // Capture the actual free-text (capped) — it's the user's own words for the job, the
    // richest "what are they looking for" signal. Non-text answers are enum/boolean values.
    track("builder_question_answered", { report: slug, question: q.key, answer: q.kind === "text" ? String(value ?? "").slice(0, 200) : value });
  }

  const outcome = useMemo(() => evaluateTriggers(triggers, answers), [triggers, answers]);

  const enabled = useMemo(() => {
    const set = new Set(report.hazards.map((h) => h.hazardId));
    for (const id of outcome.off) set.delete(id);
    for (const id of outcome.on) set.add(id);
    for (const [id, on] of Object.entries(overrides)) {
      if (on) set.add(id);
      else set.delete(id);
    }
    return set;
  }, [report, outcome, overrides]);

  const permits = useMemo(() => {
    const list = report.permitsRequired.filter((p) => !outcome.permitsRemove.includes(p));
    for (const p of outcome.permitsAdd) if (!list.includes(p)) list.push(p);
    return list;
  }, [report, outcome]);

  /** Report with answer-driven content applied (competence, substances,
   *  emergency, sequence) — what the preview and the PDF render. */
  const effectiveReport = useMemo(() => applyTriggerContent(report, outcome), [report, outcome]);

  /** Required site fields = always-required + answer-driven + permit evidence
   *  (issuer/reference/expiry) once any permit is in play. */
  const requiredKeys = useMemo(() => {
    const set = new Set(report.siteFields.filter((f) => f.required).map((f) => f.key));
    for (const k of outcome.requireFields) set.add(k);
    if (permits.length > 0) set.add("permitsRequired");
    return set;
  }, [report, outcome, permits]);

  /** Commit an in-document edit (null = restore) and capture the diff —
   *  what users change is the core report-vs-real-job alignment signal. */
  function handleEdit(key: string, next: string | null) {
    // Emptying a non-removable item (title, scope, who-at-risk) makes the
    // document invalid — treat as a restore.
    const value = isNonRemovable(key) && next !== null && !next.trim() ? null : next;
    setEdits((prev) => {
      const out = { ...prev };
      if (value === null) delete out[key];
      else out[key] = value;
      return out;
    });
    const action = value === null ? "restored" : value === "" ? "removed" : "edited";
    track("document_edited", { report: slug, key, action });
    if (value !== null) {
      fetch("/api/event", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          source: window.location.hostname.replace(/^www\./, ""),
          session_id: crypto.randomUUID(),
          event_type: "rams_edit",
          page: "/builder/",
          data: JSON.stringify({
            report: slug,
            key,
            action,
            from: originalText(report, key).substring(0, 300),
            to: value.substring(0, 300),
            email: savedEmail,
          }),
        }),
      }).catch(() => {
        /* best-effort */
      });
    }
  }

  function toggleHazard(hazardId: string) {
    const on = !enabled.has(hazardId);
    setOverrides((prev) => ({ ...prev, [hazardId]: on }));
    track("builder_hazard_toggled", { report: slug, hazard: hazardId, enabled: on });
  }

  const checks = useMemo(() => {
    const fieldChecks = report.siteFields
      .filter((f) => requiredKeys.has(f.key))
      .map((f) => ({ label: `${f.label} completed`, ok: !!(values[f.key] || "").trim() }));
    const questionCheck = {
      label: `Job questions answered (${Math.min(outcome.answered, outcome.visible.length)}/${outcome.visible.length})`,
      ok: outcome.answered >= outcome.visible.length,
    };
    // High residual risk contradicts the tolerability table ("hold work") — a
    // submission would bounce, so it fails readiness.
    const residualCheck = {
      label: "No high residual risks in scope",
      ok: report.hazards.every((h) => !enabled.has(h.hazardId) || riskScore(h.residualRisk) < 10),
    };
    const confirmChecks = CONFIRM_ITEMS.map((c) => ({ label: c.label, ok: !!confirms[c.key] }));
    // The sketch is optional — only surfaces in the checklist once one exists.
    const sketchCheck = sitePlan.length > 0 ? [{ label: "Site plan sketch attached", ok: true }] : [];
    return [
      ...fieldChecks,
      questionCheck,
      ...outcome.checks,
      { label: "At least one hazard in scope", ok: enabled.size > 0 },
      residualCheck,
      ...sketchCheck,
      ...confirmChecks,
    ];
  }, [report, values, outcome, enabled, requiredKeys, confirms, sitePlan]);
  const ready = checks.filter((c) => c.ok).length;
  const readiness = Math.round((ready / checks.length) * 100);
  const status = readinessStatus(readiness);
  const missingActions = checks.filter((c) => !c.ok).map((c) => reviewerAction(c.label));

  // Publish live builder state for the help assistant (HelpChat reads it to pick
  // context-aware starters and to tell the model which draft the user is on).
  const missingKey = missingActions.join("|");
  useEffect(() => {
    const stage =
      Object.keys(values).length === 0 && Object.keys(answers).length === 0
        ? "choosing"
        : readiness >= 65
          ? "review"
          : "filling";
    setBuilderState({
      report: entry.label,
      category: entry.category,
      trade: entry.trade,
      stage,
      readiness,
      missing: missingActions.slice(0, 4),
      emailGated: !savedEmail,
    });
    return () => setBuilderState(null);
    // missingKey stands in for missingActions (new array each render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.label, entry.category, entry.trade, readiness, missingKey, savedEmail, values, answers]);

  function download() {
    track("report_download_clicked", { report: slug, readiness, has_email: !!savedEmail });
    if (!savedEmail) {
      setGateOpen(true);
      return;
    }
    void downloadPdf(savedEmail);
  }

  /** The exact document the PDF download builds — Prince-safe HTML with edits
   *  applied, the readiness draft watermark below 100%, and the live sketch
   *  SVG. Shared by the PDF download and the in-builder rejection review so the
   *  review reads precisely what would be submitted. */
  function buildReportHtml(): string {
    // Dedicated Prince-safe document (lib/pdfHtml.ts) — the site's Tailwind
    // CSS doesn't survive Prince, so the PDF has its own plain stylesheet.
    // User edits are applied (removed items dropped, sequence renumbered).
    // Below full readiness the PDF is watermarked "incomplete draft".
    // The sketch ships as the live preview's own SVG — serialising the DOM
    // keeps the PDF pixel-identical to the preview without a second renderer.
    const planSvg = hasSketch ? document.querySelector("#builder-siteplan-print svg")?.outerHTML : undefined;
    return buildPdfHtml({
      report: applyEdits(effectiveReport, edits),
      values,
      enabled,
      permits,
      scopeLines: outcome.scopeLines,
      draft: readiness < 100 ? { readiness, missing: missingActions } : undefined,
      brand,
      issueDate: new Date().toISOString().slice(0, 10), // the download moment
      sitePlan: planSvg
        ? {
            svg: planSvg,
            caption: findCalibration(sitePlan)
              ? "Indicative scale only — set from a user-drawn reference. Verify all distances on site before relying on this plan."
              : "Diagram only — not to scale. Verify locations on site before relying on this plan.",
          }
        : undefined,
    });
  }

  /** Server-rendered PDF via DocRaptor (PrinceXML), emailed to the user as
   *  well as downloaded; window.print() as fallback. */
  async function downloadPdf(toEmail: string) {
    if (downloading) return;
    setDownloading(true);
    setEmailedTo("");
    const name = `RAMS - ${report.task} - RamsDocs`;
    try {
      const html = buildReportHtml();

      const res = await fetch("/api/pdf", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // draft routes the render to DocRaptor test mode (free, watermarked)
        // until every readiness check passes; complete docs use production.
        body: JSON.stringify({ html, name, email: toEmail, report: slug, draft: readiness < 100, readiness, missing: missingActions }),
      });
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${name}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
      setEmailedTo(toEmail);
      setLastReport(entry.label);
      track("report_downloaded", { report: slug, readiness, method: "docraptor" });
      recordDownloadSnapshot(toEmail);
      askForFeedback();
    } catch {
      track("report_downloaded", { report: slug, readiness, method: "print_fallback" });
      const prev = document.title;
      document.title = name;
      window.print();
      document.title = prev;
      askForFeedback();
    } finally {
      setDownloading(false);
    }
  }

  /** The download-outcome feedback loop: ask once per report per session,
   *  shortly after the PDF lands so it doesn't compete with the save dialog. */
  function askForFeedback() {
    try {
      if (window.sessionStorage.getItem("ramsdocs_fb_" + slug)) return;
      window.sessionStorage.setItem("ramsdocs_fb_" + slug, "1");
    } catch {
      /* private mode — still ask once for this render */
    }
    setFeedbackChoice("");
    setFeedbackRating(0);
    setFeedbackText("");
    setFeedbackStatus("idle");
    setTimeout(() => setFeedbackOpen(true), 1200);
  }

  async function submitFeedback() {
    if ((!feedbackChoice && !feedbackRating) || feedbackStatus === "loading") return;
    setFeedbackStatus("loading");
    try {
      const res = await fetch("/api/event", {
        method: "POST",
        // keepalive: the feedback dialog can be dismissed (or the tab closed)
        // the moment Send is hit; without it the POST is cancelled on unload.
        keepalive: true,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          source: window.location.hostname.replace(/^www\./, ""),
          session_id: crypto.randomUUID(),
          event_type: "rams_feedback",
          page: "/builder/",
          data: JSON.stringify({
            report: slug,
            rating: feedbackRating || null,
            choice: feedbackChoice,
            comments: feedbackText.trim().substring(0, 400),
            email: savedEmail,
            readiness,
          }),
        }),
      });
      if (!res.ok) throw new Error(String(res.status));
      track("rams_feedback", { report: slug, rating: feedbackRating, choice: feedbackChoice, has_comments: !!feedbackText.trim() });
      setFeedbackStatus("done");
      setTimeout(() => setFeedbackOpen(false), 1600);
    } catch {
      setFeedbackStatus("error");
    }
  }

  /** Alignment snapshot: how this download was configured — which hazards were
   *  switched off, what the job answers were, which fields were used. The raw
   *  material for checking how closely each report fits real jobs. */
  function recordDownloadSnapshot(toEmail: string) {
    const disabled = report.hazards.filter((h) => !enabled.has(h.hazardId)).map((h) => h.hazard);
    fetch("/api/event", {
      method: "POST",
      // keepalive: this fires right as the PDF blob download navigates the page
      // away. Without it the browser cancels the in-flight request on unload and
      // the download never lands in D1 (PostHog's report_downloaded survives only
      // because posthog-js uses sendBeacon). See appmill.dev/signups gap, Jun 2026.
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        source: window.location.hostname.replace(/^www\./, ""),
        session_id: crypto.randomUUID(),
        event_type: "rams_download",
        page: "/builder/",
        data: JSON.stringify({
          report: slug,
          email: toEmail,
          readiness,
          answers,
          hazardsOff: disabled,
          fieldsFilled: report.siteFields.filter((f) => (values[f.key] || "").trim()).map((f) => f.key),
          editsCount: Object.keys(edits).length,
          edits: editsSummary(report, edits),
        }),
      }),
    }).catch(() => {
      /* best-effort */
    });
  }

  async function submitGate(e: React.FormEvent) {
    e.preventDefault();
    if (!email.includes("@")) return;
    setGateStatus("loading");
    const params = new URLSearchParams(window.location.search);
    try {
      const res = await fetch("/api/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          source: window.location.hostname.replace(/^www\./, ""),
          intent: "report_download",
          industry: entry.trade,
          landing_page: "/builder/",
          pricing_tier_clicked: "",
          utm_source: params.get("utm_source") || "",
          utm_medium: params.get("utm_medium") || "",
          utm_campaign: params.get("utm_campaign") || "",
          utm_term: params.get("utm_term") || "",
          utm_content: params.get("utm_content") || "",
          gclid: params.get("gclid") || "",
          msclkid: params.get("msclkid") || "",
          // Attribute the server-side signup_completed to this browser person + session.
          ...phContext(),
        }),
      });
      if (!res.ok) throw new Error(String(res.status));
      const clean = email.trim().toLowerCase();
      try {
        window.localStorage.setItem(EMAIL_KEY, clean);
      } catch {
        /* private mode — remembered for this render only */
      }
      setSavedEmail(clean);
      setGateOpen(false);
      setGateStatus("idle");
      // Merge the anonymous browser person into the email-keyed person so the
      // server-side signup_completed (distinct_id = email) and this whole session
      // resolve to one profile. Must precede the unlock event so it's attributed.
      identify(clean, { email: clean, industry: entry.trade, intent: "report_download" });
      track("report_unlock_completed", { report: slug });
      googleConversion(GOOGLE_ADS.signupSendTo);
      void downloadPdf(clean);
    } catch {
      setGateStatus("error");
    }
  }

  const inputCls =
    "w-full rounded-lg border border-line bg-white px-3 py-2 text-[0.84rem] text-ink outline-none transition-colors placeholder:text-muted/60 focus:border-emerald focus:ring-2 focus:ring-emerald/15";

  function renderField(f: (typeof report.siteFields)[number]) {
    return (
      <label key={f.key} className="block">
        <span className="mb-1 block text-[0.7rem] font-semibold text-body">
          {f.label}
          {requiredKeys.has(f.key) && <span className="text-risk-high"> *</span>}
        </span>
        {f.key === "siteAddress" ? (
          <AddressField value={values[f.key] || ""} onChange={(v) => setField(f.key, v)} className={inputCls} />
        ) : TEXTAREA_FIELDS.has(f.key) ? (
          <textarea rows={2} value={values[f.key] || ""} onChange={(e) => setField(f.key, e.target.value)} className={inputCls} />
        ) : (
          <input
            type={DATE_FIELDS.has(f.key) ? "date" : "text"}
            value={values[f.key] || ""}
            onChange={(e) => setField(f.key, e.target.value)}
            // Native date inputs only open on the tiny calendar icon — make the
            // whole field open the picker (showPicker needs a user gesture).
            onClick={
              DATE_FIELDS.has(f.key)
                ? (e) => {
                    try {
                      e.currentTarget.showPicker();
                    } catch {
                      /* unsupported browser — icon still works */
                    }
                  }
                : undefined
            }
            className={`${inputCls}${DATE_FIELDS.has(f.key) ? " cursor-pointer" : ""}`}
          />
        )}
      </label>
    );
  }

  function renderQuestion(q: TriggerQuestion, depth = 0) {
    const answer = answers[q.key] || "";
    return (
      <div key={q.key} className={depth > 0 ? "ml-3 border-l-2 border-emerald/30 pl-3" : ""}>
        <p className="text-[0.78rem] font-semibold leading-snug text-ink">{q.label}</p>
        {q.kind === "yesno" && (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {["yes", "no", ...(q.notSure ? [NOT_SURE] : [])].map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => answerQuestion(q, v)}
                aria-pressed={answer === v}
                className={`rounded-full border px-4 py-1.5 text-[0.78rem] font-semibold transition-colors ${
                  answer === v ? "border-emerald bg-emerald text-white" : "border-line bg-white text-body hover:border-emerald/50"
                }`}
              >
                {YESNO_LABEL[v]}
              </button>
            ))}
          </div>
        )}
        {q.kind === "choice" && (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {(q.options || []).map((o) => (
              <button
                key={o}
                type="button"
                onClick={() => answerQuestion(q, o)}
                aria-pressed={answer === o}
                className={`rounded-full border px-3 py-1.5 text-[0.74rem] font-semibold transition-colors ${
                  answer === o ? "border-emerald bg-emerald text-white" : "border-line bg-white text-body hover:border-emerald/50"
                }`}
              >
                {o}
              </button>
            ))}
          </div>
        )}
        {q.kind === "text" && (
          <input
            type="text"
            value={answer}
            onChange={(e) => answerQuestion(q, e.target.value)}
            placeholder={q.placeholder}
            className={`mt-1.5 ${inputCls}`}
          />
        )}
        {q.followUps && answer === q.followUps.when && (
          <div className="mt-2.5 space-y-2.5">{q.followUps.questions.map((f) => renderQuestion(f, depth + 1))}</div>
        )}
      </div>
    );
  }

  // Categories in library order (every category has at least one report).
  const categories = CATEGORIES.filter((c) => REPORTS.some((r) => r.category === c.key));
  const categoryReports = REPORTS.filter((r) => r.category === entry.category);
  const catLabel = (key: string) => CATEGORIES.find((c) => c.key === key)?.label ?? key;

  // Search across ALL reports (the library now has 170+). When the box has text,
  // the picker shows flat matches from every category; otherwise it's the
  // category-tab → reports flow. Match on task label, trade and category label.
  const q = query.trim().toLowerCase();
  const searchResults = q
    ? REPORTS.filter((r) => `${r.label} ${r.trade} ${catLabel(r.category)}`.toLowerCase().includes(q)).slice(0, 60)
    : null;

  function selectCategory(key: string) {
    if (key === entry.category) return;
    const first = REPORTS.find((r) => r.category === key)!;
    // Event name kept from the trade-picker era for analytics continuity.
    track("builder_trade_selected", { trade: key });
    track("builder_category_selected", { category: key });
    selectReport(first.slug);
  }

  const pillCls = (selected: boolean) =>
    `rounded-full px-3 py-1.5 text-[0.78rem] font-semibold transition-colors ${
      selected
        ? "bg-emerald text-white shadow-[0_6px_16px_-8px_rgba(27,163,123,0.8)]"
        : "border border-line bg-white text-body hover:border-emerald/50 hover:text-ink"
    }`;

  return (
    <div className="report-builder-root mx-auto max-w-7xl px-5 py-8">
      {/* Template picker — search across the whole library, or browse by category. */}
      <div className="no-print space-y-5 rounded-2xl border border-line bg-white p-5 shadow-soft">
        <div>
          <StepLabel n={1} label={`Pick the job — ${REPORTS.length} RAMS templates`} />
          <div className="relative mt-2.5">
            <svg className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="9" cy="9" r="6" /><path d="m15 15 3 3" strokeLinecap="round" /></svg>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${REPORTS.length} templates — trade, task or keyword`}
              aria-label="Search all RAMS templates"
              className="w-full rounded-xl border border-line bg-mist/40 py-2.5 pl-9 pr-3 text-sm text-ink placeholder:text-muted focus:border-emerald focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald/20"
            />
          </div>
        </div>

        {searchResults ? (
          <div>
            <div className="flex items-center justify-between">
              <span className="text-[0.74rem] font-semibold uppercase tracking-wider text-muted">{searchResults.length}{searchResults.length === 60 ? "+" : ""} match{searchResults.length === 1 ? "" : "es"}</span>
              <button type="button" onClick={() => setQuery("")} className="text-[0.74rem] font-semibold text-emerald-deep hover:underline">Clear</button>
            </div>
            <div className="mt-2.5 flex flex-wrap gap-1.5" role="listbox" aria-label="Search results">
              {searchResults.map((r) => (
                <button key={r.slug} type="button" role="option" aria-selected={r.slug === slug} onClick={() => { selectReport(r.slug); setQuery(""); }} className={pillCls(r.slug === slug)} title={`${r.trade} · ${catLabel(r.category)}`}>
                  {r.label} <span className="font-normal opacity-60">· {r.trade}</span>
                </button>
              ))}
              {!searchResults.length && (
                <p className="text-sm text-muted">No templates match “{query}”. <button type="button" onClick={() => { setRequestStatus("idle"); setRequestOpen(true); }} className="font-semibold text-emerald-deep hover:underline">Request one</button>.</p>
              )}
            </div>
          </div>
        ) : (
          <>
            <div>
              <StepLabel n="1a" label="Browse by category" />
              <div className="mt-2.5 flex flex-wrap gap-1.5" role="tablist" aria-label="Choose a category">
                {categories.map((c) => (
                  <button key={c.key} type="button" role="tab" aria-selected={c.key === entry.category} onClick={() => selectCategory(c.key)} className={pillCls(c.key === entry.category)}>
                    {c.label} <span className="font-normal opacity-60">{REPORTS.filter((r) => r.category === c.key).length}</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <StepLabel n="1b" label={`Choose the RAMS — ${catLabel(entry.category)} (${categoryReports.length})`} />
              <div className="mt-2.5 flex flex-wrap gap-1.5" role="tablist" aria-label="Choose your report">
                {categoryReports.map((r) => (
                  <button key={r.slug} type="button" role="tab" aria-selected={r.slug === slug} onClick={() => selectReport(r.slug)} className={pillCls(r.slug === slug)} title={r.trade}>
                    {r.label}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setRequestStatus("idle");
                    setRequestOpen(true);
                    track("report_request_opened", { trade: entry.trade });
                  }}
                  className="rounded-full border border-dashed border-emerald/60 px-3 py-1.5 text-[0.78rem] font-semibold text-emerald-deep transition-colors hover:bg-emerald-tint"
                >
                  + Request a report
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-[400px_1fr]">
        {/* Config panel */}
        <div className="no-print lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:self-start lg:overflow-y-auto">
          <div className="space-y-5 rounded-2xl border border-line bg-white p-5 shadow-soft">
            <div>
              <StepLabel n={2} label="Confirm site details" />
              <p className="mt-1 text-[0.74rem] text-muted">Add the facts a reviewer needs to see. The document updates as you type.</p>
              <div className="mt-3 grid gap-2.5">
                {report.siteFields.filter((f) => requiredKeys.has(f.key)).map(renderField)}
              </div>
              <details className="mt-3 rounded-lg border border-line bg-mist/50 px-3 py-2.5">
                <summary className="cursor-pointer select-none text-[0.74rem] font-semibold text-body hover:text-ink">
                  Advanced — optional site details ({report.siteFields.filter((f) => !requiredKeys.has(f.key) && (values[f.key] || "").trim()).length}/{report.siteFields.filter((f) => !requiredKeys.has(f.key)).length} filled)
                </summary>
                <p className="mt-2 text-[0.7rem] text-muted">Only appear in the document once filled in.</p>
                <div className="mt-2 grid gap-2.5">
                  {report.siteFields.filter((f) => !requiredKeys.has(f.key)).map(renderField)}
                </div>
              </details>
            </div>

            <div>
              <StepLabel n={3} label="Answer job-specific risk questions" />
              <p className="mt-1 text-[0.74rem] text-muted">
                Your answers set the hazard scope, permits and job-specific notes in the document.
              </p>
              <div className="mt-3 space-y-3.5">{triggers.map((q) => renderQuestion(q))}</div>

              <details className="mt-4 rounded-lg border border-line bg-mist/50 px-3 py-2.5">
                <summary className="cursor-pointer select-none text-[0.74rem] font-semibold text-body hover:text-ink">
                  Advanced — adjust hazards in scope ({enabled.size}/{report.hazards.length})
                </summary>
                <p className="mt-2 text-[0.7rem] text-muted">
                  Pre-set by your answers above. Switch off only what genuinely doesn’t apply to this job.
                </p>
                <div className="mt-2 space-y-1.5">
                  {report.hazards.map((h) => {
                    const on = enabled.has(h.hazardId);
                    return (
                      <button
                        key={h.hazardId}
                        type="button"
                        role="switch"
                        aria-checked={on}
                        onClick={() => toggleHazard(h.hazardId)}
                        className={`flex w-full items-center gap-2.5 rounded-lg border px-3 py-2 text-left text-[0.78rem] font-medium transition-colors ${
                          on ? "border-emerald/40 bg-emerald-tint text-ink" : "border-line bg-white text-muted hover:border-line-2"
                        }`}
                      >
                        <span className={`relative h-4 w-7 shrink-0 rounded-full transition-colors ${on ? "bg-emerald" : "bg-line-2"}`}>
                          <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white shadow transition-all ${on ? "left-3.5" : "left-0.5"}`} />
                        </span>
                        <span className="flex-1 leading-tight">{h.hazard}</span>
                        <RiskChip rating={h.residualRisk} />
                      </button>
                    );
                  })}
                </div>
              </details>
            </div>

            <div>
              <StepLabel n={4} label="Review gaps before export" />
              <div className="mt-2.5 rounded-lg bg-mist px-3 py-2">
                <div className="flex items-center justify-between text-[0.72rem] font-medium text-ink">
                  <span>{status}</span>
                  <span className="tabular-nums text-emerald-deep">{readiness}% ready</span>
                </div>
                <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-line">
                  <div className="h-full rounded-full bg-emerald transition-[width] duration-300" style={{ width: `${readiness}%` }} />
                </div>
              </div>
              <ul className="mt-2.5 space-y-1.5">
                {checks.filter((c) => !CONFIRM_ITEMS.some((ci) => ci.label === c.label)).map((c) => {
                  const label = c.ok ? c.label : reviewerAction(c.label);
                  return (
                    <li key={c.label} className="flex items-start gap-2 text-[0.74rem] leading-snug">
                      <span
                        className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full ${
                          c.ok ? "bg-emerald text-white" : "bg-risk-medium text-ink"
                        }`}
                      >
                        {c.ok ? (
                          <svg viewBox="0 0 20 20" className="h-2.5 w-2.5" fill="none" stroke="currentColor" strokeWidth="3"><path d="m4 10 4 4 8-9" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        ) : (
                          <svg viewBox="0 0 20 20" className="h-2.5 w-2.5" fill="none" stroke="currentColor" strokeWidth="3"><path d="M10 5v6M10 14.5v.5" strokeLinecap="round" /></svg>
                        )}
                      </span>
                      <span className={c.ok ? "text-muted line-through decoration-line-2" : "font-medium text-ink"}>{label}</span>
                    </li>
                  );
                })}
              </ul>
              {/* Manual confirmations a PC reviewer expects — tick to confirm. */}
              <div className="mt-3 space-y-1.5 rounded-lg border border-line bg-mist/50 px-3 py-2.5">
                <p className="text-[0.7rem] font-semibold text-body">Confirm before submission</p>
                {CONFIRM_ITEMS.map((c) => (
                  <label key={c.key} className="flex cursor-pointer items-start gap-2 text-[0.74rem] leading-snug">
                    <input
                      type="checkbox"
                      checked={!!confirms[c.key]}
                      onChange={(e) => {
                        setConfirms((prev) => ({ ...prev, [c.key]: e.target.checked }));
                        track("builder_confirm_toggled", { report: slug, item: c.key, checked: e.target.checked });
                      }}
                      className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-emerald"
                    />
                    <span className={confirms[c.key] ? "text-muted line-through decoration-line-2" : "font-medium text-ink"}>{c.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="mt-4">
              <BrandingControls brand={brand} onChange={setBrand} />
            </div>

            <div className="border-t border-line pt-4">
              <button
                type="button"
                onClick={download}
                disabled={downloading}
                className="w-full rounded-full bg-emerald px-5 py-3 font-semibold text-white shadow-[0_8px_20px_-8px_rgba(27,163,123,0.7)] transition-colors hover:bg-emerald-deep disabled:opacity-60"
              >
                {downloading ? "Preparing your PDF…" : readiness < 100 ? "Download watermarked draft (PDF)" : "Download review-ready RAMS (PDF)"}
              </button>
              {readiness < 100 && (
                <div className="mt-2 rounded-lg border border-risk-medium/40 bg-risk-medium/10 px-3 py-2 text-[0.7rem] text-ink">
                  <p className="font-medium">This document will remain a watermarked draft — not for submission — until the following are complete:</p>
                  <ul className="mt-1 list-disc space-y-0.5 pl-4">
                    {missingActions.map((label) => (
                      <li key={label}>{label}</li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="mt-2 text-center text-[0.7rem] text-muted">
                Free during early access — no card required. You review and approve as the competent person.
              </p>
              <BuilderReview buildHtml={buildReportHtml} report={slug} readiness={readiness} />
            </div>
          </div>
        </div>

        {/* Live document */}
        <div className="min-w-0">
          <p className="no-print mb-2 flex items-center gap-1.5 text-[0.74rem] text-muted">
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 shrink-0 text-emerald-deep" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.8 2.8 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5z" /></svg>
            Click almost any text in the document — the title, scope, controls, method steps, PPE, substances, competence or emergency arrangements — to edit it for your job. Delete the text to remove an item. Changes flow into the PDF.
          </p>
          <ReportDocument report={effectiveReport} values={values} enabled={enabled} permits={permits} scopeLines={outcome.scopeLines} edits={edits} onEdit={handleEdit} draft={readiness < 100} issueDate={issueDate} brand={brand} />

          {/* Optional site plan — a single dashed affordance until used, then a
              real document section that flows into the PDF. */}
          {hasSketch ? (
            // Kept in the DOM (hidden) while the editor is open — the PDF download
            // serialises this node's SVG, and the editor replaces it in place.
            <div id="builder-siteplan-print" className={`relative mt-4 overflow-hidden rounded-2xl border border-line bg-white px-7 py-5 shadow-float ${sketchOpen ? "hidden" : ""}`}>
              <button
                type="button"
                onClick={() => setSketchOpen(true)}
                className="no-print absolute right-4 top-4 rounded-full border border-line-2 px-3 py-1.5 text-[0.74rem] font-semibold text-body hover:border-ink"
              >
                Edit sketch
              </button>
              <SitePlanPrint value={sitePlan} background={sketchBg} heading="Site plan sketch" />
            </div>
          ) : (
            !sketchOpen && (
              <button
                type="button"
                onClick={() => {
                  setSketchOpen(true);
                  track("builder_sketch_opened", { report: slug });
                }}
                className="no-print mt-4 w-full rounded-2xl border-2 border-dashed border-emerald/50 bg-white/70 px-5 py-4 text-center text-[0.85rem] font-semibold text-emerald-deep transition-colors hover:bg-emerald-tint"
              >
                + Add site plan sketch / photos (optional)
              </button>
            )
          )}

          {sketchOpen && (
            <div className="no-print mt-4">
              <SitePlanEditor
                key={slug}
                value={sitePlan}
                onChange={setSitePlan}
                background={sketchBg}
                onBackgroundChange={setSketchBg}
                defaultPreset={CATEGORY_PRESET[entry.category] ?? "all"}
                title="Site plan sketch"
                hint="Mark up the site for this job — routes, zones, key points, photos. The sketch becomes a section of the RAMS and prints with the PDF."
              />
              <button
                type="button"
                onClick={() => setSketchOpen(false)}
                className="mt-2 w-full rounded-full border border-line-2 px-5 py-2.5 text-[0.85rem] font-semibold text-body hover:border-ink"
              >
                Done — close sketch editor
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Request-a-report modal */}
      {requestOpen && (
        <div className="no-print fixed inset-0 z-[60] flex items-center justify-center bg-ink/60 px-5" role="dialog" aria-modal="true" aria-label="Request a report">
          <div className="w-full max-w-md rounded-2xl bg-white p-7 shadow-float">
            {requestStatus === "done" ? (
              <>
                <h3 className="font-display text-xl font-bold text-ink">Got it — thanks.</h3>
                <p className="mt-2 text-sm text-body">
                  Requests directly shape what we build next during early access{requestEmail.trim() ? ", and we’ll email you when it’s ready" : ""}.
                </p>
                <button type="button" onClick={() => setRequestOpen(false)} className="mt-5 w-full rounded-full bg-emerald px-5 py-3 font-semibold text-white hover:bg-emerald-deep">
                  Done
                </button>
              </>
            ) : (
              <>
                <h3 className="font-display text-xl font-bold text-ink">Request a report</h3>
                <p className="mt-2 text-sm text-body">
                  Tell us the job you need a RAMS for and we’ll add it to the library.
                </p>
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!requestText.trim() || requestStatus === "loading") return;
                    setRequestStatus("loading");
                    try {
                      const res = await fetch("/api/event", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                          source: window.location.hostname.replace(/^www\./, ""),
                          session_id: crypto.randomUUID(),
                          event_type: "report_request",
                          page: "/builder/",
                          data: JSON.stringify({ trade: entry.trade, details: requestText.trim().substring(0, 400), email: requestEmail.trim().toLowerCase() }),
                        }),
                      });
                      if (!res.ok) throw new Error(String(res.status));
                      track("report_requested", { trade: entry.trade, has_email: !!requestEmail.trim() });
                      setRequestStatus("done");
                      setRequestText("");
                    } catch {
                      setRequestStatus("error");
                    }
                  }}
                  className="mt-4 space-y-3"
                >
                  <textarea
                    rows={3}
                    required
                    autoFocus
                    value={requestText}
                    onChange={(e) => setRequestText(e.target.value)}
                    maxLength={400}
                    placeholder={`e.g. ${entry.trade} — underfloor heating installation in an occupied house`}
                    className={inputCls}
                  />
                  <input
                    type="email"
                    value={requestEmail}
                    onChange={(e) => setRequestEmail(e.target.value)}
                    placeholder="Email so we can tell you when it’s ready (optional)"
                    className={inputCls}
                  />
                  <button
                    type="submit"
                    disabled={requestStatus === "loading" || !requestText.trim()}
                    className="w-full rounded-full bg-emerald px-5 py-3 font-semibold text-white transition-colors hover:bg-emerald-deep disabled:opacity-60"
                  >
                    {requestStatus === "loading" ? "Sending…" : "Send request"}
                  </button>
                  {requestStatus === "error" && <p className="text-sm text-risk-veryhigh">Something went wrong — please try again or email hello@ramsdocs.co.uk.</p>}
                </form>
                <button type="button" onClick={() => setRequestOpen(false)} className="mt-3 w-full text-center text-sm text-muted hover:text-ink">
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Post-download feedback */}
      {feedbackOpen && (
        <div className="no-print fixed bottom-5 left-5 z-[55] w-[min(92vw,340px)] rounded-2xl border border-line bg-white p-5 shadow-float" role="dialog" aria-label="Download feedback">
          {feedbackStatus === "done" ? (
            <p className="text-sm font-semibold text-ink">Thanks — that helps us improve every report.</p>
          ) : (
            <>
              <div className="flex items-start justify-between gap-3">
                <p className="font-display text-[0.95rem] font-bold text-ink">Quick one while it saves…</p>
                <button type="button" onClick={() => setFeedbackOpen(false)} aria-label="Dismiss" className="rounded p-0.5 text-muted hover:text-ink">
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>
                </button>
              </div>
              {emailedTo && <p className="mt-1 text-[0.72rem] text-muted">PDF downloaded — a copy is on its way to {emailedTo}.</p>}
              <p className="mt-3 text-[0.78rem] font-semibold text-ink">How was the builder?</p>
              <div className="mt-1.5 flex gap-1.5" role="radiogroup" aria-label="Rating out of 5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    role="radio"
                    aria-checked={feedbackRating === n}
                    aria-label={`${n} out of 5`}
                    onClick={() => setFeedbackRating(n)}
                    className={`flex h-8 w-8 items-center justify-center rounded-full border text-[0.82rem] font-bold transition-colors ${
                      feedbackRating >= n ? "border-emerald bg-emerald text-white" : "border-line bg-white text-body hover:border-emerald/50"
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <p className="mt-3 text-[0.78rem] font-semibold text-ink">Did the RAMS cover your job?</p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {["Covered everything", "Mostly — I had to tweak it", "Missing things"].map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setFeedbackChoice(c)}
                    aria-pressed={feedbackChoice === c}
                    className={`rounded-full border px-3 py-1.5 text-[0.74rem] font-semibold transition-colors ${
                      feedbackChoice === c ? "border-emerald bg-emerald text-white" : "border-line bg-white text-body hover:border-emerald/50"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
              {(feedbackChoice || feedbackRating > 0) && (
                <>
                  <textarea
                    rows={2}
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    maxLength={400}
                    placeholder="What was missing, wrong or annoying? (optional)"
                    className={`mt-2.5 ${inputCls}`}
                  />
                  <button
                    type="button"
                    onClick={() => void submitFeedback()}
                    disabled={feedbackStatus === "loading"}
                    className="mt-2 w-full rounded-full bg-emerald px-4 py-2 text-[0.82rem] font-semibold text-white transition-colors hover:bg-emerald-deep disabled:opacity-60"
                  >
                    {feedbackStatus === "loading" ? "Sending…" : "Send feedback"}
                  </button>
                </>
              )}
              {feedbackStatus === "error" && <p className="mt-2 text-[0.74rem] text-risk-veryhigh">Something went wrong — please try again.</p>}
            </>
          )}
        </div>
      )}

      {/* Email unlock gate */}
      {gateOpen && (
        <div className="no-print fixed inset-0 z-[60] flex items-center justify-center bg-ink/60 px-5" role="dialog" aria-modal="true" aria-label="Unlock your download">
          <div className="w-full max-w-md rounded-2xl bg-white p-7 shadow-float">
            <h3 className="font-display text-xl font-bold text-ink">Where should we send your RAMS?</h3>
            <p className="mt-2 text-sm text-body">
              We’ll email you a copy of the finished PDF so it’s always to hand, as well as downloading it now.
              Free during early access — we’ll only email you about RamsDocs.
            </p>
            <form onSubmit={submitGate} className="mt-4 space-y-3">
              <input
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Work email"
                className={inputCls}
              />
              <button
                type="submit"
                disabled={gateStatus === "loading"}
                className="w-full rounded-full bg-emerald px-5 py-3 font-semibold text-white transition-colors hover:bg-emerald-deep disabled:opacity-60"
              >
                {gateStatus === "loading" ? "Unlocking…" : "Unlock my download"}
              </button>
              {gateStatus === "error" && <p className="text-sm text-risk-veryhigh">Something went wrong — please try again.</p>}
            </form>
            <button type="button" onClick={() => setGateOpen(false)} className="mt-3 w-full text-center text-sm text-muted hover:text-ink">
              Not now
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function StepLabel({ n, label }: { n: number | string; label: string }) {
  return (
    <div className="flex items-center gap-2 text-[0.7rem] font-semibold uppercase tracking-wider text-emerald-deep">
      <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-emerald px-1 text-[0.6rem] text-white">{n}</span>
      {label}
    </div>
  );
}
